import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useGlobalNavigation } from '../../contexts/GlobalNavigationContext';
import './TabHighlight.css';

// Context para gerenciar o estado global de mudanças não salvas
const UnsavedChangesContext = createContext();

/**
 * Provider para gerenciar mudanças não salvas em múltiplas tabs
 * Apenas registra no contexto global, sem modal de confirmação entre tabs
 */
export const UnsavedChangesProvider = ({ children }) => {
  const [unsavedTabs, setUnsavedTabs] = useState(new Set());

  // Conecta com o contexto global
  const { registerUnsavedChanges, clearUnsavedChanges } = useGlobalNavigation();

  // Função para aplicar highlight visual nas tabs
  const updateTabHighlight = useCallback((tabKey, hasUnsaved) => {
    // Mapeia as chaves das tabs para os eventKeys das tabs
    const tabMapping = {
      'anamnese': '1',
      'receituario': '2', 
      'pedidosexame': '3',
      'atestado': '4',
      'exame': '5',
      'avaliacao': '5',
      'dieta': '5'
    };

    const eventKey = tabMapping[tabKey];
    if (!eventKey) return;

    // Usa setTimeout para garantir que o DOM foi atualizado
    setTimeout(() => {
      // Encontra o elemento da tab pelo eventKey - múltiplas tentativas
      let tabElement = document.querySelector(`[data-rr-ui-event-key="${eventKey}"]`);
      
      // Se não encontrou, tenta outras formas de localizar
      if (!tabElement) {
        tabElement = document.querySelector(`#controlled-tab-example-tab-${eventKey}`);
      }
      
      // Se ainda não encontrou, tenta pela estrutura do Bootstrap
      if (!tabElement) {
        const tabContainer = document.querySelector('#controlled-tab-example');
        if (tabContainer) {
          const allTabs = tabContainer.querySelectorAll('.nav-link');
          tabElement = allTabs[parseInt(eventKey) - 1];
        }
      }

      if (!tabElement) {
        return;
      }

      if (hasUnsaved) {
        // Adiciona classe de não salvo
        tabElement.classList.add('tab-unsaved');
        tabElement.classList.remove('tab-saved');
        
        // Adiciona animação de flash se não estiver ativa
        if (!tabElement.classList.contains('active')) {
          tabElement.classList.add('tab-highlight-flash');
          setTimeout(() => {
            if (tabElement) {
              tabElement.classList.remove('tab-highlight-flash');
            }
          }, 1000);
        }
      } else {
        // Remove classe de não salvo
        tabElement.classList.remove('tab-unsaved', 'tab-highlight-flash');
        tabElement.classList.add('tab-saved');
      }
    }, 100); // Pequeno delay para garantir que o DOM foi atualizado
  }, []);

  // Função para re-aplicar todos os highlights (chamada após mudanças de tab)
  const reapplyAllHighlights = useCallback(() => {
    unsavedTabs.forEach(tabKey => {
      updateTabHighlight(tabKey, true);
    });
  }, [unsavedTabs, updateTabHighlight]);

  // Intervalo para garantir que os highlights persistam
  useEffect(() => {
    const interval = setInterval(() => {
      if (unsavedTabs.size > 0) {
        reapplyAllHighlights();
      }
    }, 2000); // Re-aplica a cada 2 segundos se houver tabs não salvas

    return () => clearInterval(interval);
  }, [unsavedTabs.size, reapplyAllHighlights]);

  // Observer para detectar mudanças no DOM das tabs
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          // Se uma tab mudou de classe (ativa/inativa), re-aplica highlights
          const target = mutation.target;
          if (target.classList.contains('nav-link')) {
            setTimeout(() => reapplyAllHighlights(), 50);
          }
        }
      });
    });

    // Listener para evento customizado de re-aplicação
    const handleReapplyHighlights = () => {
      setTimeout(() => reapplyAllHighlights(), 50);
    };

    window.addEventListener('reapplyHighlights', handleReapplyHighlights);

    // Observa mudanças nas tabs
    const tabContainer = document.querySelector('#controlled-tab-example');
    if (tabContainer) {
      observer.observe(tabContainer, {
        attributes: true,
        subtree: true,
        attributeFilter: ['class']
      });
    }

    return () => {
      observer.disconnect();
      window.removeEventListener('reapplyHighlights', handleReapplyHighlights);
    };
  }, [reapplyAllHighlights]);

  // Registra uma tab como tendo mudanças não salvas
  const registerUnsavedTab = useCallback((tabKey) => {
    setUnsavedTabs(prev => {
      const newSet = new Set([...prev, tabKey]);
      // Notifica o contexto global
      if (newSet.size > 0) {
        registerUnsavedChanges('Ficha do Paciente');
      }
      // Aplica highlight visual
      updateTabHighlight(tabKey, true);
      return newSet;
    });
  }, [registerUnsavedChanges, updateTabHighlight]);

  // Remove uma tab da lista de não salvas
  const unregisterUnsavedTab = useCallback((tabKey) => {
    setUnsavedTabs(prev => {
      const newSet = new Set(prev);
      newSet.delete(tabKey);
      // Se não há mais tabs não salvas, limpa o contexto global
      if (newSet.size === 0) {
        clearUnsavedChanges();
      }
      // Remove highlight visual
      updateTabHighlight(tabKey, false);
      return newSet;
    });
  }, [clearUnsavedChanges, updateTabHighlight]);

  // Verifica se uma tab específica tem mudanças não salvas
  const hasUnsavedChanges = useCallback((tabKey) => {
    return unsavedTabs.has(tabKey);
  }, [unsavedTabs]);

  // Verifica se alguma tab tem mudanças não salvas
  const hasAnyUnsavedChanges = useCallback(() => {
    return unsavedTabs.size > 0;
  }, [unsavedTabs]);

  const value = {
    registerUnsavedTab,
    unregisterUnsavedTab,
    hasUnsavedChanges,
    hasAnyUnsavedChanges,
    unsavedTabsCount: unsavedTabs.size
  };

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
    </UnsavedChangesContext.Provider>
  );
};

/**
 * Hook para usar o contexto de mudanças não salvas
 */
export const useUnsavedChangesContext = () => {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChangesContext deve ser usado dentro de UnsavedChangesProvider');
  }
  return context;
};